/*
  gwp.js — free gym bag controller.  Built 2026-09-02.

  Headless. It renders nothing and decides nothing: snippets/gwp-summary.liquid
  evaluates the rule server-side on every cart render and states, in data
  attributes, what quantity of the gift SHOULD be in the cart. This file's only
  job is to make the cart agree with that verdict.

  Why it is built this way
  ------------------------
  * Liquid owns the rule.  Horizon re-renders the cart section on every mutation,
    so the server already re-evaluates for free. One source of truth, and the
    progress bar and gift line need no JavaScript at all.
  * POST /cart/update.js, never /cart/add.js.  update.js sets an ABSOLUTE
    quantity, so it is idempotent — a duplicated request cannot put two bags in
    the cart. One endpoint covers add, remove and trim.
  * The dispatched event action is 'update' or 'remove', never 'add'.
    assets/cart-drawer.js auto-opens the drawer on `event.action === 'add'`
    (auto_open_cart_drawer is true on this store), so an 'add' would pop the
    drawer open unprompted during a page-load back-fill, and would report the
    free bag to GA4/Meta as a genuine add-to-cart.
  * The event is dispatched on `document`, not on the <cart-items-component>.
    assets/component-cart-items.js short-circuits with `if (event.target === this)
    return;`, so dispatching on the component would stop it re-rendering itself.
  * The payload lives as own properties on the event object (action, context,
    lines, promise), NOT under event.detail — that is the shape
    CartLinesUpdateEvent uses and the shape every listener reads.
*/
(function () {
  'use strict';

  if (window.__gwpControllerLoaded) return;
  window.__gwpControllerLoaded = true;

  /* Per-page-load circuit breaker. The rule is written so a cart converges in a
     single mutation; anything beyond a couple means Liquid and the cart disagree
     in a loop, and stopping is better than hammering /cart/update.js. */
  var MAX_MUTATIONS = 8;
  var mutations = 0;
  var inFlight = false;

  function cartSectionIds() {
    var ids = [];
    var nodes = document.querySelectorAll('cart-items-component[data-section-id]');
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  function updateUrl() {
    if (window.Theme && Theme.routes && Theme.routes.cart_update_url) {
      return Theme.routes.cart_update_url;
    }
    return '/cart/update.js';
  }

  function reconcile(el) {
    if (inFlight) return;

    var variantId = el.getAttribute('data-variant-id');
    var current = parseInt(el.getAttribute('data-current'), 10);
    var desired = parseInt(el.getAttribute('data-desired'), 10);

    if (!variantId || isNaN(current) || isNaN(desired)) return;
    if (current === desired) return; // the common case: nothing to do

    if (mutations >= MAX_MUTATIONS) {
      console.warn('[gwp] circuit breaker tripped — cart and rule disagree, stopping.');
      return;
    }

    mutations++;
    inFlight = true;

    var updates = {};
    updates[variantId] = desired;

    var body = { updates: updates };
    var ids = cartSectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }

    /* Announce before the network call, exactly as the theme's own components do,
       and hand listeners a promise they can await for the rendered sections. */
    var settle;
    var event = new Event('shopify:cart:lines-update', { bubbles: true });
    event.__gwp = true;
    event.action = desired === 0 ? 'remove' : 'update';
    event.context = 'cart';
    event.lines = [{ merchandiseId: variantId, quantity: desired }];
    event.promise = new Promise(function (resolve) {
      settle = resolve;
    });
    document.dispatchEvent(event);

    fetch(updateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update.js returned ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        inFlight = false;
        settle({
          cart: cart,
          detail: {
            sections: cart.sections,
            items: cart.items,
            itemCount: cart.item_count,
            source: 'gwp-controller',
            didError: false
          }
        });
      })
      .catch(function (error) {
        inFlight = false;
        console.error('[gwp] could not reconcile the gift line:', error);
        /* Always settle. An unsettled promise leaves every cart listener hanging
           and the drawer never re-renders. */
        settle({ cart: null, detail: { source: 'gwp-controller', didError: true } });
      });
  }

  /* The upsell form.  It is authored as a real <form action="/cart/add"> so it
     still works with JavaScript off, but a plain submit would hard-navigate to
     /cart and throw the shopper out of the drawer they were about to check out
     from. Intercept and add over AJAX instead.

     action is 'add' here, unlike the gift back-fill: this IS a genuine
     add-to-cart, so it should reach GA4/Meta as one, and the drawer is already
     open so cart-drawer.js auto-opening is a no-op. */
  document.addEventListener('submit', function (submitEvent) {
    var form = submitEvent.target;
    if (!form || !form.classList || !form.classList.contains('gwp-upsell')) return;

    submitEvent.preventDefault();

    var button = form.querySelector('.gwp-upsell__add');
    var idField = form.querySelector('input[name="id"]');
    if (!idField) return;
    if (button) {
      button.disabled = true;
      button.textContent = 'Adding';
    }

    var payload = { items: [{ id: idField.value, quantity: 1 }] };
    var ids = cartSectionIds();
    if (ids.length) {
      payload.sections = ids.join(',');
      payload.sections_url = window.location.pathname;
    }

    var settle;
    var event = new Event('shopify:cart:lines-update', { bubbles: true });
    event.__gwp = true;
    event.action = 'add';
    event.context = 'cart';
    event.lines = [{ merchandiseId: idField.value, quantity: 1 }];
    event.promise = new Promise(function (resolve) {
      settle = resolve;
    });
    document.dispatchEvent(event);

    fetch(window.Theme && Theme.routes && Theme.routes.cart_add_url ? Theme.routes.cart_add_url : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/add.js returned ' + response.status);
        return response.json();
      })
      .then(function (result) {
        settle({
          cart: result,
          detail: {
            sections: result.sections,
            items: result.items,
            source: 'gwp-upsell',
            didError: false
          }
        });
      })
      .catch(function (error) {
        console.error('[gwp] upsell add failed:', error);
        settle({ cart: null, detail: { source: 'gwp-upsell', didError: true } });
        /* The section will not re-render, so hand the control back to the user
           rather than leaving a dead button. */
        if (button) {
          button.disabled = false;
          button.textContent = 'Add';
        }
      });
  });

  /* Take the gift row's product links out of service.

     The cart line's title and image are anchors to the product page, and the gift
     is a Rs 0 product. Leaving them live handed every shopper the URL of a free
     product they could then add from directly — and the offer's abuse posture
     rests on that page being hard to find, so the cart was advertising exactly
     what it was meant to hide. Arshad found this 2026-09-02.

     CSS in gwp-summary.liquid sets pointer-events:none, which stops the click.
     This also strips the href and removes the anchors from the tab order, so the
     link is gone for keyboard and assistive tech too, not merely unclickable. */
  function neutraliseGiftLinks(variantId) {
    var selector =
      '.cart-items__table-row:has(cart-quantity-selector-component[data-variant-id="' +
      variantId +
      '"]) a[href]';
    var links;
    try {
      links = document.querySelectorAll(selector);
    } catch (e) {
      return; // :has() unsupported — the server-side rule still governs the cart
    }
    for (var i = 0; i < links.length; i++) {
      links[i].removeAttribute('href');
      links[i].setAttribute('tabindex', '-1');
      links[i].setAttribute('aria-disabled', 'true');
    }
  }

  /* Re-run after every cart change, not just on page load.

     connectedCallback alone is NOT enough. Horizon morphs the cart section in
     place (assets/morph.js, hydration mode), so on a cart update the existing
     <gwp-controller> has its data-* attributes PATCHED rather than being removed
     and re-inserted — the element is never disconnected, so connectedCallback
     never fires again and the verdict is read once, at page load, and then
     ignored for the rest of the session.

     Live consequence, measured on real orders 2026-09-02: #1183 (Rs 50,000 of
     ISO 100) and #1185 (2 units) both QUALIFIED and shipped with no bag, because
     the shopper added to cart and checked out without a full page load in
     between. And #1187 kept a bag after dropping to a single non-qualifying item,
     for the mirror-image reason. Reproduced on the preview: after Add to Cart the
     controller read data-desired="1" data-current="0" and still did nothing for
     6 seconds.

     Own events are skipped via the __gwp tag, and the small cushion lets the
     theme's own listener finish morphing before the fresh verdict is read. */
  function afterCartRender() {
    var el = document.querySelector('gwp-controller');
    if (!el) return;
    var v = el.getAttribute('data-variant-id');
    if (v) neutraliseGiftLinks(v);
    reconcile(el);
  }

  document.addEventListener('shopify:cart:lines-update', function (evt) {
    if (evt && evt.__gwp) return; // our own mutation; the morph settles it
    var settleThen = function () {
      window.setTimeout(afterCartRender, 200);
    };
    if (evt && evt.promise && typeof evt.promise.then === 'function') {
      evt.promise.then(settleThen, settleThen);
    } else {
      settleThen();
    }
  });

  if (!customElements.get('gwp-controller')) {
    customElements.define(
      'gwp-controller',
      class GwpController extends HTMLElement {
        connectedCallback() {
          var self = this;
          var giftVariant = this.getAttribute('data-variant-id');
          /* setTimeout, NOT requestAnimationFrame. rAF does not fire while the
             tab is hidden, so a store opened in a background tab — a product
             opened in a new tab, a restored session — would never back-fill the
             gift until the tab was focused. Verified on the draft preview
             2026-09-02: document.visibilityState 'hidden', rAF did not fire.

             The link neutering has to sit in here too, not run synchronously.
             This element is rendered ABOVE the line items, so during a morph
             connectedCallback fires before the gift row exists and a synchronous
             query matches nothing. Verified 2026-09-02: pointer-events applied
             (CSS, server-rendered, no timing) but hrefs survived. A macrotask
             lets the morph finish first, which is the same reason reconcile is
             deferred. */
          setTimeout(function () {
            if (giftVariant) neutraliseGiftLinks(giftVariant);
            reconcile(self);
          }, 0);
        }
      }
    );
  }
})();
